import { api } from '@/backoffice/redux/api/_generated/api/backoffice-api.generated';
import { identApi } from '@/shared/redux/api/ident/ident-api';
import { TRtkApiTags } from '@/shared/types/api.types';

type TBackofficeApiTags =
  | 'BackofficeTenant'
  | 'SuperUser'
  | 'SuperUserRole'
  | 'BackofficeResourceGroup'
  | 'SuperUserUiPreferences'
  | 'Ec2OrchestratorGlobalConfig'
  | 'Ec2OrchestratorIdeConfig'
  | 'Ec2OrchestratorConfigHistory'
  | 'SuperTenantFeatureFlagCatalog'
  | 'SuperTenant';

export const backofficeApi = api.enhanceEndpoints({
  addTagTypes: [
    'BackofficeTenant',
    'SuperUser',
    'SuperUserRole',
    'BackofficeResourceGroup',
    'SuperUserUiPreferences',
    'Ec2OrchestratorGlobalConfig',
    'Ec2OrchestratorIdeConfig',
    'Ec2OrchestratorConfigHistory',
    'SuperTenantFeatureFlagCatalog',
    'SuperTenant',
  ],
  endpoints: {
    ReadSuperTenantFeatureFlagCatalogs: {
      providesTags: [{ type: 'SuperTenantFeatureFlagCatalog', id: 'LIST' }],
    },
    CreateSuperTenantFeatureFlag: {
      invalidatesTags: [
        { type: 'SuperTenantFeatureFlagCatalog', id: 'LIST' },
        { type: 'SuperTenant', id: 'CATALOG' },
      ],
    },
    UpdateSuperTenantFeatureFlag: {
      invalidatesTags: [
        { type: 'SuperTenantFeatureFlagCatalog', id: 'LIST' },
        { type: 'SuperTenant', id: 'CATALOG' },
      ],
    },
    // Cascade is the only path that strips the flag from tenants, so the tenants it
    // reported have to drop their cached feature-flag list with it.
    DeleteSuperTenantFeatureFlag: {
      invalidatesTags: (result) => [
        { type: 'SuperTenantFeatureFlagCatalog', id: 'LIST' },
        { type: 'SuperTenant', id: 'CATALOG' },
        ...(result?.deleteSuperTenantFeatureFlag.affectedTenantIds ?? []).map(
          (tenantId) => ({ type: 'BackofficeTenant' as const, id: tenantId }),
        ),
      ],
    },
    // What the tenant assignment drawer offers. Publishing, hiding or deleting a
    // flag changes that list, so all three mutations have to invalidate it.
    ReadSuperTenant: {
      providesTags: [{ type: 'SuperTenant', id: 'CATALOG' }],
    },
    ReadTenantsForSuperTenant: {
      providesTags: [{ type: 'BackofficeTenant', id: 'LIST' }],
    },
    ReadTenantForSuperTenant: {
      providesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
      ],
    },
    ReadTenantFeatureFlags: {
      providesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
      ],
    },
    ReadTenantForUpdate: {
      providesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
      ],
    },
    ActivateTenant: {
      invalidatesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
      ],
    },
    UpdateBackofficeTenant: {
      invalidatesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
        { type: 'BackofficeTenant', id: 'LIST' },
      ],
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(identApi.util.invalidateTags(['Tenant']));
      },
    },
    UpdateTenantSecuritySettings: {
      invalidatesTags: (_, __, args) => [
        { type: 'BackofficeTenant', id: args.tenantId },
      ],
      // The security settings tab reads the tenant through ident.
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        await queryFulfilled;
        dispatch(identApi.util.invalidateTags(['Tenant']));
      },
    },
    ReadAllSuperUsers: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUser', id: 'LIST' },
        ];

        if (result?.readAllUsers) {
          result.readAllUsers.items.forEach((user) => {
            tags.push({ type: 'SuperUser', id: user.userId });
          });
        }

        return tags;
      },
    },
    // Same SuperUser tags as ReadAllSuperUsers so renaming a user invalidates
    // this lookup too and the history tab stops showing a stale name.
    ReadSuperUserNames: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUser', id: 'LIST' },
        ];

        if (result?.readSuperUserNames) {
          result.readSuperUserNames.items.forEach((user) => {
            tags.push({ type: 'SuperUser', id: user.userId });
          });
        }

        return tags;
      },
    },
    ReadSuperUser: {
      providesTags: (_, __, arg) => [{ type: 'SuperUser', id: arg.userId }],
    },
    ReadAllSuperUserRoles: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUserRole', id: 'LIST' },
        ];

        if (result?.readAllUserRoles) {
          result.readAllUserRoles.items.forEach((userRole) => {
            tags.push({ type: 'SuperUserRole', id: userRole.userRoleId });
          });
        }

        return tags;
      },
    },
    ReadSuperUsersForSelect: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUser', id: 'LIST' },
        ];

        result?.readAllUsers.items.forEach((user) => {
          tags.push({ type: 'SuperUser', id: user.userId });
        });

        return tags;
      },
    },
    ReadSuperUserRolesForSelect: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUserRole', id: 'LIST' },
        ];

        result?.readAllUserRoles.items.forEach((userRole) => {
          tags.push({ type: 'SuperUserRole', id: userRole.userRoleId });
        });

        return tags;
      },
    },
    ReadSuperUserRole: {
      providesTags: (result) => [
        { type: 'SuperUserRole', id: result?.readUserRole?.userRoleId },
      ],
    },
    ReadSuperUserRolesForOwnUser: {
      providesTags: [{ type: 'SuperUserRole', id: 'LIST' }],
    },
    ReadSuperUsersForUserRole: {
      providesTags: (result, _, arg) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'SuperUserRole', id: arg.userRoleId },
        ];

        if (result?.readUsersForUserRole) {
          result.readUsersForUserRole.items.forEach((user) => {
            tags.push({ type: 'SuperUser', id: user.userId });
          });
        }

        return tags;
      },
    },
    ReadSuperUserRoleUserLinksForUser: {
      providesTags: (_, __, arg) => [{ type: 'SuperUser', id: arg.userId }],
    },
    ReadSuperUserRoleUserLinksForUserRole: {
      providesTags: (_, __, arg) => [
        { type: 'SuperUserRole', id: arg.userRoleId },
      ],
    },
    CreateSuperUser: {
      invalidatesTags: [{ type: 'SuperUser', id: 'LIST' }],
    },
    UpdateSuperUser: {
      invalidatesTags: (_, __, arg) => [
        { type: 'SuperUser', id: arg.userId },
        { type: 'SuperUser', id: 'LIST' },
      ],
    },
    DeleteSuperUser: {
      invalidatesTags: [{ type: 'SuperUser', id: 'LIST' }],
    },
    CreateSuperUserRole: {
      invalidatesTags: [{ type: 'SuperUserRole', id: 'LIST' }],
    },
    UpdateSuperUserRole: {
      invalidatesTags: (_, __, arg) => [
        { type: 'SuperUserRole', id: arg.userRoleId },
        { type: 'SuperUserRole', id: 'LIST' },
      ],
    },
    DeleteSuperUserRole: {
      invalidatesTags: [{ type: 'SuperUserRole', id: 'LIST' }],
    },
    CreateSuperUserRoleUserLink: {
      invalidatesTags: (_, __, arg) => [
        { type: 'SuperUser', id: arg.userId },
        { type: 'SuperUserRole', id: arg.userRoleId },
        { type: 'SuperUser', id: 'LIST' },
      ],
    },
    DeleteSuperUserRoleUserLink: {
      invalidatesTags: (_, __, arg) => [
        { type: 'SuperUser', id: arg.userId },
        { type: 'SuperUserRole', id: arg.userRoleId },
        { type: 'SuperUserRole', id: 'LIST' },
      ],
    },
    UpdateSuperUserRoleUserLink: {
      invalidatesTags: (_, __, arg) => [
        { type: 'SuperUser', id: arg.userId },
        { type: 'SuperUserRole', id: arg.userRoleId },
      ],
    },
    ReadBackofficeResourceGroupsToBuildTree: {
      providesTags: [{ type: 'BackofficeResourceGroup', id: 'LIST' }],
    },
    // Shares the list tag with ReadAllBackofficeResourceGroups, so the group
    // mutations refresh it too.
    ReadBackofficeResourceGroupsForSelect: {
      providesTags: [{ type: 'BackofficeResourceGroup' as const, id: 'LIST' }],
    },
    ReadAllBackofficeResourceGroups: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'BackofficeResourceGroup', id: 'LIST' },
        ];

        if (result?.readAllResourceGroups) {
          result.readAllResourceGroups.items.forEach((group) => {
            tags.push({ type: 'BackofficeResourceGroup', id: group.groupId });
          });
        }

        return tags;
      },
    },
    ReadBackofficeResourceGroupById: {
      providesTags: (result, __, arg) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'BackofficeResourceGroup', id: 'LIST' },
          { type: 'BackofficeResourceGroup', id: arg.groupId },
        ];

        if (result?.readSubGroupsForResourceGroup) {
          result.readSubGroupsForResourceGroup.items?.forEach((subgroup) => {
            tags.push({
              type: 'BackofficeResourceGroup',
              id: subgroup.groupId,
            });
          });
        }

        return tags;
      },
    },
    ReadSubGroupsForBackofficeResourceGroup: {
      providesTags: (_, __, arg) => [
        // LIST catches subgroup delete (delete only invalidates LIST).
        { type: 'BackofficeResourceGroup', id: 'LIST' },
        { type: 'BackofficeResourceGroup', id: arg.groupId },
      ],
    },
    CreateBackofficeResourceGroup: {
      invalidatesTags: (result) => {
        const newGroupParentId =
          result?.createResourceGroup.parentGroupId ?? '';

        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'BackofficeResourceGroup', id: 'LIST' },
        ];

        if (newGroupParentId) {
          tags.push({ type: 'BackofficeResourceGroup', id: newGroupParentId });
        }

        return tags;
      },
    },
    UpdateBackofficeResourceGroup: {
      invalidatesTags: ['BackofficeResourceGroup'],
    },
    DeleteBackofficeResourceGroup: {
      invalidatesTags: [{ type: 'BackofficeResourceGroup', id: 'LIST' }],
    },
    ReadBackofficeGroupResourceCounts: {
      providesTags: (_, __, arg) => [
        { type: 'SuperUser', id: 'LIST' },
        { type: 'SuperUserRole', id: 'LIST' },
        { type: 'BackofficeTenant', id: 'LIST' },
        // LIST also catches subgroup delete (which only invalidates LIST).
        { type: 'BackofficeResourceGroup', id: 'LIST' },
        { type: 'BackofficeResourceGroup', id: arg.groupId },
      ],
    },
    ReadBackofficeUngroupedResourceCounts: {
      providesTags: [
        { type: 'SuperUser', id: 'LIST' },
        { type: 'SuperUserRole', id: 'LIST' },
        { type: 'BackofficeTenant', id: 'LIST' },
      ],
    },
    ReadSuperUserUiPreferences: {
      providesTags: ['SuperUserUiPreferences'],
    },
    UpdateSuperUserUiPreferences: {
      invalidatesTags: ['SuperUserUiPreferences'],
    },
    ReadGlobalOrchestratorConfig: {
      providesTags: [{ type: 'Ec2OrchestratorGlobalConfig', id: 'SETTINGS' }],
    },
    UpdateGlobalOrchestratorConfig: {
      invalidatesTags: [
        { type: 'Ec2OrchestratorGlobalConfig', id: 'SETTINGS' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    ListIdeOrchestratorConfigs: {
      providesTags: (result) => {
        const tags: TRtkApiTags<TBackofficeApiTags> = [
          { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        ];

        if (result?.listIdeOrchestratorConfigs) {
          result.listIdeOrchestratorConfigs.forEach((config) => {
            tags.push({
              type: 'Ec2OrchestratorIdeConfig',
              id: config.ideName,
            });
          });
        }

        return tags;
      },
    },
    ListAllIdeOrchestratorConfigOverrides: {
      providesTags: [{ type: 'Ec2OrchestratorIdeConfig', id: 'LIST' }],
    },
    ListConfigChangeHistory: {
      providesTags: [{ type: 'Ec2OrchestratorConfigHistory', id: 'LIST' }],
    },
    // No tag: the result is bounded by its time range and refetches whenever
    // the range or IDE filter changes.
    FleetCapacityMetrics: {},
    // No tag: nothing in back-office writes run status -- ec2-orchestration-service
    // does, on its own schedule -- so there is no mutation here that could
    // invalidate it. The tab polls and exposes refetch instead.
    ReadOrchestratorRunStatus: {},
    CreateIdeOrchestratorConfig: {
      invalidatesTags: [
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    UpdateIdeOrchestratorConfig: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Ec2OrchestratorIdeConfig', id: arg.ideName },
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    DeleteIdeOrchestratorConfig: {
      invalidatesTags: [
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    CreateIdeOrchestratorConfigOverride: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Ec2OrchestratorIdeConfig', id: arg.override.ideName },
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    UpdateIdeOrchestratorConfigOverride: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Ec2OrchestratorIdeConfig', id: arg.ideName },
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
    DeleteIdeOrchestratorConfigOverride: {
      invalidatesTags: (_, __, arg) => [
        { type: 'Ec2OrchestratorIdeConfig', id: arg.ideName },
        { type: 'Ec2OrchestratorIdeConfig', id: 'LIST' },
        { type: 'Ec2OrchestratorConfigHistory', id: 'LIST' },
      ],
    },
  },
});

export const {
  useReadTenantsForSuperTenantQuery,
  useReadTenantForSuperTenantQuery,
  useLazyReadTenantForSuperTenantQuery,
  useReadTenantFeatureFlagsQuery,
  useReadTenantForUpdateQuery,
  useLoginTenantForSuperTenantMutation,
  useActivateTenantMutation,
  useUpdateBackofficeTenantMutation,
  useUpdateTenantSecuritySettingsMutation,
  useReadAllSuperUserRolesQuery,
  useReadSuperUserRolesForSelectQuery,
  useReadSuperUserRoleQuery,
  useReadSuperUserRolesForOwnUserQuery,
  useReadAllSuperUsersQuery,
  useReadSuperUsersForSelectQuery,
  useReadSuperUserQuery,
  useReadSuperUsersForUserRoleQuery,
  useReadSuperUserRoleUserLinksForUserQuery,
  useReadSuperUserRoleUserLinksForUserRoleQuery,
  useCreateSuperUserMutation,
  useUpdateSuperUserMutation,
  useDeleteSuperUserMutation,
  useCreateSuperUserRoleMutation,
  useUpdateSuperUserRoleMutation,
  useDeleteSuperUserRoleMutation,
  useCreateSuperUserRoleUserLinkMutation,
  useDeleteSuperUserRoleUserLinkMutation,
  useUpdateSuperUserRoleUserLinkMutation,
  useReadSuperTenantQuery,
  useReadBackofficeResourceGroupByIdQuery,
  useReadAllBackofficeResourceGroupsQuery,
  useReadBackofficeResourceGroupsForSelectQuery,
  useReadSubGroupsForBackofficeResourceGroupQuery,
  useReadBackofficeResourceGroupsToBuildTreeQuery,
  useReadBackofficeResourceGroupNamesForRoleRowQuery,
  useReadBackofficeResourceGroupNameByIdQuery,
  useLazyReadBackofficeResourceGroupNameByIdQuery,
  useCreateBackofficeResourceGroupMutation,
  useUpdateBackofficeResourceGroupMutation,
  useDeleteBackofficeResourceGroupMutation,
  useReadBackofficeGroupResourceCountsQuery,
  useReadBackofficeUngroupedResourceCountsQuery,
  useReadSuperUserUiPreferencesQuery,
  useUpdateSuperUserUiPreferencesMutation,
  useReadGlobalOrchestratorConfigQuery,
  useUpdateGlobalOrchestratorConfigMutation,
  useListIdeOrchestratorConfigsQuery,
  useCreateIdeOrchestratorConfigMutation,
  useUpdateIdeOrchestratorConfigMutation,
  useDeleteIdeOrchestratorConfigMutation,
  useCreateIdeOrchestratorConfigOverrideMutation,
  useUpdateIdeOrchestratorConfigOverrideMutation,
  useDeleteIdeOrchestratorConfigOverrideMutation,
  useListAllIdeOrchestratorConfigOverridesQuery,
  useListConfigChangeHistoryQuery,
  useFleetCapacityMetricsQuery,
  useReadSuperUserNamesQuery,
  useReadOrchestratorRunStatusQuery,
  useReadUiSpeedSummaryQuery,
  useReadSreHubSummaryQuery,
  useReadSuperTenantFeatureFlagCatalogsQuery,
  useCreateSuperTenantFeatureFlagMutation,
  useUpdateSuperTenantFeatureFlagMutation,
  useDeleteSuperTenantFeatureFlagMutation,
} = backofficeApi;
